import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, MessageSquareText, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/Btn";
import { api, errText } from "@/lib/api";

const AREAS = ["General", "Check In", "Get Help Now", "AI notes", "History & reports", "Something's broken"];

export function FeedbackForm() {
  const [rating, setRating] = useState(null);
  const [area, setArea] = useState("General");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/pilot-feedback", { rating, area, message: message.trim() });
      toast.success("Thank you — your feedback helps shape Triggerific");
      setMessage(""); setRating(null); setArea("General");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="feedback-form">
      <div className="flex gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`feedback-star-${n}`} aria-label={`${n} star${n > 1 ? "s" : ""}`}
            className="p-1 transition-transform hover:scale-110"><Star size={26} className={rating >= n ? "text-[#E6C288]" : "text-t-border2"} fill={rating >= n ? "#E6C288" : "none"} /></button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {AREAS.map((a) => (
          <button key={a} onClick={() => setArea(a)} data-testid={`feedback-area-${a.split(" ")[0].toLowerCase()}`}
            className={`px-3.5 h-8 rounded-full text-[13px] font-semibold border transition-colors ${area === a ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-on2 border-t-border"}`}>{a}</button>
        ))}
      </div>
      <textarea className="tf-input mt-3" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={3000}
        placeholder="What's working, what's confusing, what you'd love to see…" data-testid="feedback-message-input" />
      <Btn size="md" className="mt-3" label="Send feedback" loading={busy} disabled={!message.trim()} onClick={send} testId="feedback-send-button" />
    </div>
  );
}

const cell = (v) => { const s = v == null ? "" : /^[=+\-@\t\r]/.test(String(v)) ? `'${v}` : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
function exportCsv(list) {
  const rows = [["Date", "Name", "Email", "Area", "Rating (1-5)", "Message"], ...list.map((f) => [f.timestamp.slice(0, 16).replace("T", " "), f.name, f.email, f.area || "General", f.rating ?? "", f.message])];
  const url = URL.createObjectURL(new Blob(["\ufeff" + rows.map((r) => r.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `triggerific-pilot-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function PilotFeedbackAdmin() {
  const qc = useQueryClient();
  const { data: list = [] } = useQuery({ queryKey: ["pilot-feedback"], queryFn: async () => (await api.get("/admin/pilot-feedback")).data });
  const [arm, setArm] = useState(null);
  const del = async (id) => {
    if (arm !== id) return setArm(id);
    await api.delete(`/admin/pilot-feedback/${id}`);
    setArm(null);
    qc.invalidateQueries({ queryKey: ["pilot-feedback"] });
  };
  const rated = list.filter((f) => f.rating);
  const avg = rated.length ? (rated.reduce((a, f) => a + f.rating, 0) / rated.length).toFixed(1) : null;
  return (
    <div className="tf-card p-5" data-testid="admin-pilot-feedback">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-bold text-t-on"><MessageSquareText size={18} className="text-t-brand" /> Pilot feedback</p>
        <Btn size="md" variant="secondary" icon={<Download size={16} />} label="Export feedback CSV" disabled={!list.length} onClick={() => exportCsv(list)} testId="pilot-feedback-export" />
      </div>
      <p className="text-sm text-t-muted mt-0.5 mb-3">Shared by testers from Settings · {list.length} message{list.length === 1 ? "" : "s"}{avg ? ` · average ${avg}/5` : ""}</p>
      {!list.length ? <p className="text-sm text-t-muted">No feedback yet.</p> : (
        <ul className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
          {list.map((f, i) => (
            <li key={f.id} className="rounded-2xl bg-t-surface2 border border-t-border px-4 py-3" data-testid={`pilot-feedback-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-t-on">{f.name || f.email} <span className="font-normal text-t-muted">· {f.area || "General"}{f.rating ? ` · ${"★".repeat(f.rating)}` : ""}</span></p>
                <button onClick={() => del(f.id)} onBlur={() => setArm(null)} className={`inline-flex items-center gap-1 text-xs font-semibold transition-colors ${arm === f.id ? "text-[var(--hot)]" : "text-t-muted hover:text-[var(--hot)]"}`} aria-label="Delete feedback" data-testid={`pilot-feedback-delete-${i}`}><Trash2 size={15} />{arm === f.id ? "Tap again to delete" : null}</button>
              </div>
              <p className="mt-1 text-sm text-t-on2 whitespace-pre-line">{f.message}</p>
              <p className="mt-1 text-xs text-t-muted">{f.email} · {new Date(f.timestamp).toLocaleString()}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
